import { writable, derived, type Readable } from 'svelte/store';
import type { BboxId } from '../utils/bbox';

/**
 * 문서 전역 bbox 선택 상태를 공유하는 store.
 * 모든 페이지가 동일한 상태를 읽어 페이지 간 드래그 선택이 유지된다.
 */
interface BboxSelectionState {
    /** 선택된 bbox ID 집합 */
    selectedBboxIds: Set<BboxId>;

    /** 드래그 선택 진행 여부 */
    isDragging: boolean;

    /** mouseup 직후 외부 클릭으로 선택이 삭제되는 것을 막는 전역 플래그 */
    isMouseUp: boolean;
}

/** bbox 선택 상태를 관리하는 커스텀 store */
const createBboxSelectionStore = () => {
    const initialState: BboxSelectionState = {
        selectedBboxIds: new Set(),
        isDragging: false,
        isMouseUp: false,
    };

    const { subscribe, update } = writable<BboxSelectionState>(initialState);

    return {
        subscribe,

        /** 드래그 선택 시작 */
        startDrag: () => {
            update(state => ({
                ...state,
                isDragging: true,
            }));
        },

        /** 드래그 종료 */
        endDrag: () => {
            update(state => ({
                ...state,
                isDragging: false,
            }));
        },

        /** mouseup 직후 선택 해제가 발생하지 않도록 플래그 설정 */
        setMouseUpFlag: () => {
            update(state => ({
                ...state,
                isMouseUp: true,
            }));
        },

        /** setMouseUpFlag 이후 마이크로태스크에서 호출 */
        clearMouseUpFlag: () => {
            update(state => ({
                ...state,
                isMouseUp: false,
            }));
        },

        selectBbox: (bboxId: BboxId) => {
            update(state => {
                if (state.selectedBboxIds.has(bboxId)) return state;
                const nextSelected = new Set(state.selectedBboxIds);
                nextSelected.add(bboxId);

                return {
                    ...state,
                    selectedBboxIds: nextSelected,
                };
            });
        },

        /** 모든 선택 및 호버 상태 초기화 */
        clearSelection: () => {
            update(state => ({
                ...state,
                selectedBboxIds: state.selectedBboxIds.size > 0 ? new Set() : state.selectedBboxIds,
            }));
        },
    };
};

/** 문서 전역에서 공유하는 단일 인스턴스 */
export const bboxSelectionStore = createBboxSelectionStore();

/** 최소 한 개 이상 선택됐는지 여부 */
export const hasSelection: Readable<boolean> = derived(bboxSelectionStore, $store => $store.selectedBboxIds.size > 0);
